import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, TrendingUp, Image, FileText, MessageSquare, Phone, Star } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const LandingPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axiosInstance.post(endpoint, { username, password });
      
      toast.success(isLogin ? 'Erfolgreich angemeldet!' : 'Konto erstellt!');
      onLogin(response.data.access_token, response.data.username);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const testimonials = [
    {
      name: 'Michael Weber',
      role: 'Finanzberater',
      text: 'Diese App hat unsere Lead-Generierung revolutioniert! In nur 2 Monaten haben wir 300% mehr qualifizierte Anfragen erhalten.',
      rating: 5
    },
    {
      name: 'Sarah Klein',
      role: 'Versicherungsmaklerin',
      text: 'Die AdCopies sind professionell und treffen genau den richtigen Ton. Unsere Conversion-Rate ist um 150% gestiegen!',
      rating: 5
    },
    {
      name: 'Thomas Müller',
      role: 'Vermögensberater',
      text: 'Endlich eine Lösung, die speziell für Finanzdienstleister entwickelt wurde. Die Anleitungen sind Gold wert!',
      rating: 5
    },
    {
      name: 'Julia Schmidt',
      role: 'Kreditvermittlerin',
      text: 'Die KI-generierten Bilder sind beeindruckend professionell. Unsere Anzeigen stechen jetzt wirklich heraus!',
      rating: 5
    }
  ];

  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="w-full max-w-md shadow-2xl" data-testid="auth-card">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {isLogin ? 'Willkommen zurück' : 'Konto erstellen'}
              </h2>
              <p className="text-gray-600">FinAds Creator</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Benutzername"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="username-input"
                  className="h-12"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="h-12"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-lg font-semibold"
                disabled={loading}
                data-testid="submit-auth-button"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                {loading ? 'Lädt...' : (isLogin ? 'Anmelden' : 'Registrieren')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Noch kein Konto? Registrieren' : 'Bereits registriert? Anmelden'}
              </button>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowAuth(false)}
                className="text-sm text-gray-600 hover:text-gray-700"
                data-testid="back-to-landing"
              >
                ← Zurück zur Startseite
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '600px'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-full p-4">
                <Sparkles className="w-12 h-12" />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Werbeanzeigen für Finanzdienstleister
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              KI-gestützte AdCopy-Erstellung, professionelle Bildgenerierung und Expertenwissen für erfolgreiche Meta-Kampagnen
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              className="h-14 px-8 text-lg font-semibold shadow-2xl hover:shadow-xl"
              data-testid="get-started-btn"
              style={{
                background: 'white',
                color: '#667eea'
              }}
            >
              Jetzt starten
            </Button>
            <Button
              onClick={() => setShowAuth(true)}
              size="lg"
              variant="outline"
              className="h-14 px-8 text-lg font-semibold bg-white/20 hover:bg-white/30 text-white border-2 border-white"
            >
              Bereits Kunde? Anmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
          Alles was Sie für erfolgreiche Werbeanzeigen brauchen
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="hover:shadow-xl transition-all duration-300 border-2 border-purple-100" data-testid="feature-adcopy">
            <CardContent className="pt-8 pb-8">
              <div className="bg-purple-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">AdCopy Generator</h3>
              <p className="text-gray-600">
                KI-generierte Werbetexte in verschiedenen Stilen. Mit oder ohne Emojis. Perfekt für Finanzdienstleister.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-xl transition-all duration-300 border-2 border-purple-100" data-testid="feature-images">
            <CardContent className="pt-8 pb-8">
              <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Image className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Bildgenerierung</h3>
              <p className="text-gray-600">
                Professionelle Werbebilder mit KI erstellen. Mehrere Variationen in Sekunden.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-xl transition-all duration-300 border-2 border-purple-100" data-testid="feature-recommendations">
            <CardContent className="pt-8 pb-8">
              <div className="bg-green-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Lead Ad vs. Landing Page</h3>
              <p className="text-gray-600">
                Erfahren Sie, welche Strategie für Ihr Ziel am besten geeignet ist.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gradient-to-b from-purple-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
            Was unsere Kunden sagen
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-all duration-300" data-testid={`testimonial-${idx}`}>
                <CardContent className="pt-8 pb-8">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-800">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Bereit, Ihre Werbeanzeigen zu optimieren?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Starten Sie noch heute und erstellen Sie professionelle Werbeanzeigen in Minuten
          </p>
          <Button
            onClick={() => setShowAuth(true)}
            size="lg"
            className="h-14 px-8 text-lg font-semibold"
            data-testid="cta-get-started-btn"
            style={{
              background: 'white',
              color: '#667eea'
            }}
          >
            Kostenlos loslegen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
